import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/client';

const ThemeContext = createContext(null);

// Stored choice is 'light' | 'dark' | 'system'. 'system' resolves to the OS preference.
function resolveTheme(choice) {
  if (choice === 'system') {
    return (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light';
  }
  return choice === 'dark' ? 'dark' : 'light';
}

function applyTheme(choice) {
  document.documentElement.setAttribute('data-theme', resolveTheme(choice));
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const saved = localStorage.getItem('theme') || 'light';
    applyTheme(saved);
    return saved;
  });

  // Load from DB once on mount (if authenticated)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    api.get('/auth/me').then(res => {
      const t = res.data.theme || 'light';
      localStorage.setItem('theme', t);
      applyTheme(t);
      setThemeState(t);
    }).catch(() => {});
  }, []);

  // While in 'system' mode, follow live OS scheme changes.
  useEffect(() => {
    if (theme !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((t) => {
    localStorage.setItem('theme', t);
    applyTheme(t);
    setThemeState(t);
    api.patch('/auth/me', { theme: t }).catch(() => {});
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
